// Copyright (c) Ping. All rights reserved.
//
// Message-bubble speech tail, web (+ generic) variant. Faithful to the
// Element/Matrix approach the user referenced (`.mx_EventTile_line:after`
// with `mask-image`): a small box FILLED with the bubble color and MASKED
// to the tail silhouette, anchored just outside the bubble's bottom-outer
// corner (bottom:-2, end/start:-2, 13×15, mask-size 100%).
//
// Rendered as a raw absolutely-positioned DOM node — the equivalent of an
// `::after` pseudo-element — so it never participates in layout/measure
// (no reflow) and is never an async `<Image>`. The parent bubble is
// `position: relative` (MessageBubble.styles) so this anchors to it.

import React from 'react';
import { View } from 'react-native';

export type TailSide = 'left' | 'right';

export interface BubbleTailProps {
  readonly fill: string;
  readonly side: TailSide;
}

const IS_WEB = typeof document !== 'undefined' && typeof window !== 'undefined';

// Tail silhouettes in a 13×15 box. The straight inner edge sits flush
// against the bubble's bottom-outer corner; the concave outer edge curves
// out to a soft point — the classic speech-bubble flick. Right = outgoing
// (self), Left = incoming (other), a horizontal mirror.
export const TAIL_PATH_RIGHT = 'M0 0 C0 8 5 13.5 13 15 L0 15 Z';
export const TAIL_PATH_LEFT = 'M13 0 C13 8 8 13.5 0 15 L13 15 Z';

export function BubbleTail({ fill, side }: BubbleTailProps): JSX.Element {
  if (IS_WEB) {
    // Render a REAL inline <svg> via React DOM — no mask-image, no
    // data-URI encoding to get wrong, no async <Image>. React renders
    // lowercase 'svg'/'path' as genuine SVG DOM (RN-Web doesn't intercept
    // them), so this paints the exact tail shape filled with the bubble
    // color. Absolutely positioned just outside the bubble's bottom-outer
    // corner (the bubble is `position: relative`). Equivalent to the
    // Element `::after` tail the user referenced.
    return React.createElement(
      'svg',
      {
        width: 13,
        height: 15,
        viewBox: '0 0 13 15',
        'aria-hidden': true,
        style: {
          position: 'absolute',
          bottom: -2,
          pointerEvents: 'none',
          ...(side === 'right' ? { right: -2 } : { left: -2 }),
        },
      },
      React.createElement('path', {
        d: side === 'right' ? TAIL_PATH_RIGHT : TAIL_PATH_LEFT,
        fill,
      }),
    );
  }

  // Non-web non-macOS fallback (ios/android — not a primary surface here).
  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        bottom: 1,
        width: 10,
        height: 10,
        backgroundColor: fill,
        borderRadius: 2,
        transform: [{ rotate: '45deg' }],
        ...(side === 'right' ? { right: -2 } : { left: -2 }),
      }}
    />
  );
}
